import React from 'react';

import compareAsc from 'date-fns/compareAsc';
import range from 'lodash/range';

// Components
import Advertisement from '../Advertisement/Advertisement';
import Loader, { LoaderColors } from '../Loader/Loader';
import Time from '../Time/Time';
import VideoList, { IVideoItem } from '../VideoList/VideoList';

import { ReactComponent as ArrowRight } from '../../assets/arrow_right-24px.svg';

// Config
import {
  getPortraitById,
  getProfileUrlById,
  KEEPERS_MAX_COUNT,
} from '../../config';

// Utils
import {
  getPlaybackVideos,
  IBrightcovePlaybackVideo,
} from '../../util/brightcove-api';
import { classList, machineName, prettifyOunces } from '../../util/util';

// Types
import {
  AdvertisementVariant,
  AnglerId,
  IBasstrakkAngler,
  ICatchesSummary,
} from '../../types';

interface IProps {
  anglerId: AnglerId;
  anglerName: string;
  catchData: ICatchesSummary;
  count: number;
}
interface IState {
  latestVideos: IVideoItem[];
  loading: boolean;
  portraitLoading: boolean;
}
interface IAnglerCatchProps {
  // Table data cell
  item: any;
  // Array of Strings describing header rows
  headerRows: string[];
  // Indexed position
  index: number;
}

interface IAnglerProps {
  anglerData: IBasstrakkAngler[];
}

/**
 * Renders the expanded angler row when a row is clicked
 */
class AnglerExpanded extends React.PureComponent<IProps, IState> {
  static defaultProps = {
    anglerName: '',
  };

  public state: IState = {
    latestVideos: [],
    loading: true,
    portraitLoading: true,
  };

  videoSubscription = {
    isActive: false,
    subscription: null,
  };

  videoPlaybackQueryTimer: any = null;

  public currentYear = new Date().getFullYear();

  // Empty catch blocks to show when <5 catches exist
  private initialCatchBlocks = range(5).reduce((columns, num) => {
    columns[`catch${num + 1}`] = <span className="catch"></span>;
    return columns;
  }, {} as any);

  public getLatestVideos = () => {
    const { anglerName } = this.props;

    if (anglerName) {
      this.videoSubscription.isActive = true;

      getPlaybackVideos(anglerName).then(res => {
        if (this.videoSubscription.isActive) {
          const parsedVideos = res.videos.map(
            (video: IBrightcovePlaybackVideo) => ({
              alt: video.name,
              image: video.poster,
              title: video.name,
              videoId: video.id,
            })
          );

          this.setState({ latestVideos: parsedVideos, loading: false });
        }
      });
    }
  };

  public setLatestVideos = (latestVideos: IVideoItem[]) =>
    this.setState({
      latestVideos,
      loading: false,
    });

  public portraitLoaded = () => this.setState({ portraitLoading: false });

  public async componentDidMount() {
    this.getLatestVideos();

    /**
     * @TODO figure out business logic for how frequently we want to requery the videoPlayback API
     */
    this.videoPlaybackQueryTimer = setInterval(this.getLatestVideos, 60 * 1000);
  }

  public componentWillUnmount() {
    if (this.videoPlaybackQueryTimer) {
      clearInterval(this.videoPlaybackQueryTimer);
      this.videoPlaybackQueryTimer = null;
    }
  }

  public addDefaultSrc({ target }){
    target.src = '/wp-content/themes/bassmaster-theme/client/src/images/shield-logo.svg';
    target.className = 'shield-fallback';
  }

  public render() {
    const { loading, latestVideos, portraitLoading } = this.state;

    const { anglerId, anglerName, catchData, count } = this.props;

    /**
     * @todo get a smaller image that fits this box
     */
    const portrait = getPortraitById(anglerId);
    const profile = getProfileUrlById(anglerId);

    const anglerCatches =
      catchData.keptTop5TotalWeight !== 0
        ? [
            {
              ...catchData.keptTop5
                // Sort catches from earliest to latest
                .sort((catch1, catch2) =>
                  compareAsc(new Date(catch1.Time), new Date(catch2.Time))
                )
                // Turn catches into object that Table needs to render
                .reduce((columns, fishCatch, idx) => {
                  columns[`catch${idx + 1}`] = (
                    <>
                      <div className="angler-catch__weight">
                        {fishCatch.FishWeightPdsOz}
                      </div>
                      <Time time={fishCatch.Time} />
                    </>
                  );
                  return columns;
                }, this.initialCatchBlocks),
              // Final column for Table
              'Catch Total': (
                <>
                  <div className="angler-catch__total">
                    {prettifyOunces(catchData.keptTop5TotalWeight)}
                  </div>
                  <div className="angler-catch__keepers">
                    Keepers {catchData.keptTop5.length}/{KEEPERS_MAX_COUNT}
                  </div>
                </>
              ),
            },
          ]
        : [
            {
              'No Catches': (
                <div className="angler-catch__no-catches">
                  <strong>No individual fish data for this angler</strong>
                </div>
              ),
            },
          ];

    // <Table tableData={anglerCatches} smartStyles={false} />
    // <AnglerStats anglerData={anglerCatches} />

    return (
      <div className="angler-expanded" itemID={`${count + 1}`}>
        <div className="angler-expanded__left">
          <div className="angler-profile">
            <Loader loading={portraitLoading} color={LoaderColors.LIGHT} />
            <img
              src={portrait}
              alt="Portrait of angler"
              onLoad={this.portraitLoaded}
              onError={this.addDefaultSrc}
            />
            <div
              className={classList(
                'angler-profile__link--button',
                portraitLoading && 'angler-profile__link--hidden'
              )}
            >
              <a target="_blank" href={profile}>
                Angler Information
                <ArrowRight width="32" />
              </a>
            </div>
          </div>
        </div>
        <div className="angler-expanded__middle">
          <div className="angler-expanded__container">
            <div className="angler-catches">
              <div className="angler-catches__title">Caught Today</div>
              <AnglerExpandedRows anglerData={anglerCatches} />
            </div>
            <div className="angler-videos">
              {!loading ? (
                <VideoList anglerName={anglerName || ''} items={latestVideos} />
              ) : (
                <Loader loading={loading} color={LoaderColors.LIGHT} />
              )}
            </div>
          </div>
        </div>
        <div className="angler-expanded__right">
          <div>
            <Advertisement
              variant={AdvertisementVariant.BOX}
              count={count + 1}
            />
          </div>
        </div>
      </div>
    );
  }
}

/**
 *
 * @param props
 */
const AnglerExpandedRows = (props: IAnglerProps) => {
  const { anglerData } = props;

  // Header rows for table.
  const headerRows = Object.keys(anglerData[0]);

  const items = anglerData.map(row => {
    const rowItems = Object.values(row);
    return rowItems.map((item, rowItemIndex) => (
      <AnglerCatch
        key={rowItemIndex}
        item={item}
        headerRows={headerRows}
        index={rowItemIndex}
      />
    ));
  });

  return (
    <div className="angler-catches__today" data-testid="table-row">
      {items}
    </div>
  );
};

/**
 * Renders each catch item.
 *
 * Separate component so we can provide different props.
 * @param props
 */
const AnglerCatch = (props: IAnglerCatchProps) => {
  const { item, headerRows, index } = props;
  let classes = `angler-catch ${machineName(headerRows[index])}`;
  if (headerRows[0] === 'No Catches') {
    classes = 'angler-catch__no-catches';
  }
  return (
    <div className={classes} key={headerRows[index]}>
      {item}
    </div>
  );
};

export default AnglerExpanded;
